/** Text for one monitoring bank card in its failed state. */
export interface BankLoadErrorText {
  /** Replaces the bank name in the card header, a one-line ellipsised `<h6>`. */
  headline: string;
  /** The header tooltip: the full bank id, what happened, and the status. */
  tooltip: string;
}

// Enough of a UUID to tell cards apart on a crowded grid.
const ID_PREFIX_LENGTH = 8;

const readStatus = (error: unknown): unknown =>
  typeof error === "object" && error !== null && "status" in error
    ? (error as { status: unknown }).status
    : undefined;

// A number is an HTTP status. A string is one of RTK Query's sentinels for a
// failure that is not an HTTP error status (FETCH_ERROR, TIMEOUT_ERROR,
// PARSING_ERROR) and is shown as it came, so it stays greppable. Anything else
// has no status.
const describeStatus = (status: unknown): string => {
  if (typeof status === "number" && Number.isInteger(status)) {
    return `HTTP ${status}`;
  }
  if (typeof status === "string" && status !== "") {
    return status;
  }
  return "HTTP status unavailable";
};

/**
 * Maps the error a failed bank query returned to the header text and tooltip for its card.
 *
 * "Not found — stale layout" is only true for a 404: the bank is absent. A 401 is an
 * authorization denial (for example the role's menu lacks Edge Management > Banks > View) and a
 * 403 is a per-bank scope denial. Reporting either as a deleted bank sends triage after the
 * wrong thing.
 *
 * Only `status` is read, never the body, and only a numeric one is classified. RTK Query's string
 * sentinels are shown verbatim, so a `PARSING_ERROR` (a response that could not be decoded) is
 * never taken for a 404 even if it carries an `originalStatus` of 404. Reading only the status
 * also means a 404 that did not come from LiftCloud, such as an ingress answering for a missing
 * route, reads as "not found" like any other.
 */
export const describeBankLoadError = (error: unknown, bankId: string): BankLoadErrorText => {
  const idPrefix = `${bankId.slice(0, ID_PREFIX_LENGTH)}…`;
  const status = readStatus(error);
  const label = describeStatus(status);

  switch (status) {
    case 404:
      return {
        headline: `⚠ Bank not found: ${idPrefix}`,
        tooltip: `Bank ${bankId} not found (${label}). Stale layout — bank no longer exists in DB.`,
      };
    case 401:
      return {
        headline: `⚠ No permission to view bank: ${idPrefix}`,
        tooltip: `You do not have permission to view bank ${bankId} (${label}).`,
      };
    case 403:
      return {
        headline: `⚠ Bank outside your role's scope: ${idPrefix}`,
        tooltip: `Bank ${bankId} is outside your role's bank scope (${label}).`,
      };
    default:
      return {
        headline: `⚠ Bank could not be loaded (${label}): ${idPrefix}`,
        tooltip: `Bank ${bankId} could not be loaded (${label}).`,
      };
  }
};
